import { MOCK_AWARDED_BADGES } from './badge-awarded-mocks';
import { countLatestModuleBadges, countTotalBadgesEarned } from './badge-stats';
import { toDrawerBadges } from './badge-transforms';
import { type AwardedBadge, type BadgesResponse, DrawerBadge } from './badges';

/** Readable module names for the sample response's module ids (Storybook only). */
export const MOCK_MODULE_NAMES: Record<string, string> = {
  '264d6f00-ad1f-4c0e-9979-c391df883631':
    'Module 3 - Understanding The Number System',
  'module-001-a': 'Module 1 - Understanding The Number System',
  '9a0dff51-e972-4491-9072-4310bcda09a3': 'Mastering Tables',
};

/** Sample `/user/v1/badges` response (mirrors the dev API for a young student). */
export const MOCK_BADGES_RESPONSE: BadgesResponse = {
  eligible_badges: [
    {
      badge_id: 'first_step',
      badge_name: 'First Step',
      type: 'auto',
      category: 'Early Momentum',
      description: 'Completed first Bhanzu session',
      repeatable: false,
    },
    {
      badge_id: 'strong_start',
      badge_name: 'Strong Start',
      type: 'auto',
      category: 'Early Momentum',
      description:
        'Attended first 5 sessions with >=80% attendance (4+ sessions attended)',
      repeatable: false,
    },
    {
      badge_id: 'streak_warrior',
      badge_name: 'Streak Warrior',
      type: 'auto',
      category: 'Attendance Consistency',
      description: 'Attended 5 consecutive sessions without a miss',
      repeatable: false,
    },
    {
      badge_id: 'streak_champion',
      badge_name: 'Streak Champion',
      type: 'auto',
      category: 'Attendance Consistency',
      description: 'Attended 10 consecutive sessions without a miss',
      repeatable: false,
    },
    {
      badge_id: 'streak_legend',
      badge_name: 'Streak Legend',
      type: 'auto',
      category: 'Attendance Consistency',
      description: 'Attended 25 consecutive sessions without a miss',
      repeatable: false,
    },
    {
      badge_id: 'unstoppable',
      badge_name: 'Unstoppable',
      type: 'auto',
      category: 'Attendance Consistency',
      description: 'Attended 50 consecutive sessions without a miss',
      repeatable: false,
    },
    {
      badge_id: 'on_time_star',
      badge_name: 'On-Time Star',
      type: 'auto',
      category: 'Attendance Consistency',
      description:
        'Joined on time 10 times in a row (within 2 minutes of start time)',
      repeatable: false,
    },
    {
      badge_id: 'super_simba',
      badge_name: 'Super Simba',
      type: 'manual',
      category: 'In-Class',
      description:
        'Kids admire lions for bravery; Simba is widely recognized and symbolizes courage.',
      repeatable: true,
    },
    {
      badge_id: 'kind_koala',
      badge_name: 'Kind Koala',
      type: 'manual',
      category: 'In-Class',
      description: 'Koalas are calm, friendly, and caring.',
      repeatable: true,
    },
    {
      badge_id: 'einstein_spark',
      badge_name: 'Einstein Spark',
      type: 'manual',
      category: 'In-Class',
      description:
        'Einstein always thought fast and connected ideas instantly.',
      repeatable: true,
    },
    {
      badge_id: 'ryan_jr',
      badge_name: 'Ryan Jr',
      type: 'manual',
      category: 'In-Class',
      description:
        'Just like Ryan in history of math, super curious in understanding why.',
      repeatable: true,
    },
    {
      badge_id: 'active_ninja',
      badge_name: 'Active Ninja',
      type: 'manual',
      category: 'In-Class',
      description: 'Newton kept experimenting until he found answers.',
      repeatable: true,
    },
    {
      badge_id: 'gentle_giraffe',
      badge_name: 'Gentle Giraffe',
      type: 'manual',
      category: 'In-Class',
      description: 'Giraffes stay calm even when things get noisy.',
      repeatable: true,
    },
    {
      badge_id: 'joyful_jupiter',
      badge_name: 'Joyful Jupiter',
      type: 'manual',
      category: 'In-Class',
      description: 'You brightened the class like Jupiter in the sky.',
      repeatable: true,
    },
    {
      badge_id: 'brainy_bee',
      badge_name: 'Brainy Bee',
      type: 'manual',
      category: 'In-Class',
      description: 'Precision and problem-solving like a true inventor.',
      repeatable: true,
    },
    {
      badge_id: 'dolphin_memory',
      badge_name: 'Dolphin Memory',
      type: 'manual',
      category: 'In-Class',
      description: 'Dolphins never forget.',
      repeatable: true,
    },
    {
      badge_id: 'rising_star',
      badge_name: 'Rising Star',
      type: 'manual',
      category: 'Special Recognition',
      description:
        "Teacher's choice for exceptional progress - max 1 per student per month.",
      repeatable: false,
    },
  ],
  awarded_badges: MOCK_AWARDED_BADGES,
};

/** Earned + locked badges for the drawer "All Badges" view (Storybook). */
export const MOCK_DRAWER_BADGES: DrawerBadge[] = toDrawerBadges(
  MOCK_BADGES_RESPONSE,
  MOCK_MODULE_NAMES
);

/** Same eligible set, but nothing earned (all locked) — empty-state stories. */
export const MOCK_ALL_LOCKED_BADGES: DrawerBadge[] = toDrawerBadges({
  eligible_badges: MOCK_BADGES_RESPONSE.eligible_badges,
  awarded_badges: [],
});

/** Derived stats for the sample response. */
export const MOCK_DRAWER_STATS = {
  totalBadgesEarned: countTotalBadgesEarned(MOCK_BADGES_RESPONSE),
  moduleBadges: countLatestModuleBadges(MOCK_BADGES_RESPONSE),
};

/** Convenience for stories/tests: build UI badges from a simple progress list. */
export const buildDrawerBadges = (
  progress: Array<{ badgeId: string; count: number }>
): DrawerBadge[] => {
  const awarded_badges: AwardedBadge[] = progress.map(({ badgeId, count }) => ({
    badge_id: badgeId,
    badge_name: '',
    type: 'manual',
    category: '',
    celebrated: true,
    awarded_count: count,
    awarded_on: Math.floor(Date.now() / 1000),
    awarded_by: 'mock',
    module_id: '',
  }));
  return toDrawerBadges({
    eligible_badges: MOCK_BADGES_RESPONSE.eligible_badges,
    awarded_badges,
  });
};
